import type { SanityLinkValue } from "@ouster/shared";

export type SanityLinkAdapterResult = {
  download?: boolean;
  href?: string;
  newWindow?: boolean;
  text?: string;
};

export type CraftLink = SanityLinkAdapterResult & {
  __typename?: string;
};

function withLeadingSlash(value?: string) {
  if (!value) {
    return undefined;
  }

  // Some overview pages store their uri with a leading slash ("/downloads"),
  // so a naive "/" + uri yields "//downloads", which browsers read as a
  // protocol-relative URL to the host "downloads". Collapse to a single slash.
  return `/${value.replace(/^\/+/, "")}`;
}

function appendAnchor(href: string | undefined, anchor?: string) {
  if (!href || !anchor) {
    return href;
  }

  return `${href}#${anchor}`;
}

function getInternalHref(link: SanityLinkValue) {
  const internal = link.internal;
  const href = withLeadingSlash(internal?.link?.uri || internal?.link?.slug);

  return appendAnchor(href, internal?.sectionTarget);
}

function getFallbackText(link: SanityLinkValue, href?: string) {
  switch (link.kind) {
    case "internal":
      return link.internal?.link?.title || link.internal?.link?.uri || link.internal?.link?.slug || href;
    case "external":
      return link.external || href;
    case "email":
      return link.email || href;
    case "phone":
      return link.phone || href;
    case "file":
      return link.file?.asset?.originalFilename || href;
    default:
      return href;
  }
}

export function resolveSanityLinkHref(link?: SanityLinkValue | null) {
  if (!link) {
    return undefined;
  }

  if (link.href) {
    // The shared GROQ projection precomputes internal hrefs as "/" + uri.
    return link.kind === "internal" && link.href.startsWith("/") ? withLeadingSlash(link.href) : link.href;
  }

  switch (link.kind) {
    case "internal":
      return getInternalHref(link);
    case "external":
      return link.external;
    case "email":
      return link.email ? `mailto:${link.email}` : undefined;
    case "phone":
      return link.phone ? `tel:${link.phone}` : undefined;
    case "file":
      return link.file?.asset?.url;
    default:
      return undefined;
  }
}

export function adaptSanityLink(link?: SanityLinkValue | null): SanityLinkAdapterResult | undefined {
  if (!link) {
    return undefined;
  }

  const href = resolveSanityLinkHref(link);
  if (!href) {
    return undefined;
  }

  return {
    download: link.download ?? (link.kind === "file" ? link.canDownload : undefined),
    href,
    newWindow: link.newWindow ?? link.openInNewTab,
    text: link.text || link.customText || getFallbackText(link, href),
  };
}

export function toCraftLink(link?: SanityLinkValue | null, fallbackText?: string): CraftLink | undefined {
  const resolved = adaptSanityLink(link);

  if (!resolved) {
    return undefined;
  }

  return {
    __typename: "baseLink_Link",
    ...resolved,
    text: resolved.text || fallbackText,
  };
}

export function toCraftLinkArray(link?: SanityLinkValue | null, fallbackText?: string) {
  const resolved = toCraftLink(link, fallbackText);
  return resolved ? [resolved] : [];
}
